import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import api from '../services/api'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  FileText,
  Image as ImageIcon,
  FileSearch,
  Lock,
  Unlock,
  X,
  Download,
  Loader2
} from 'lucide-react'

const SECTIONS = [
  {
    key: 'A',
    name: 'Employee Header',
    fields: [
      { key: 'employee_code', label: 'Employee Code' },
      { key: 'date_of_joining', label: 'Date of Joining' }
    ]
  },
  {
    key: 'B',
    name: 'Personal Information',
    fields: [
      { key: 'full_name', label: 'Full Name' },
      { key: 'father_husband_name', label: "Father's/Husband's Name" },
      { key: 'current_designation', label: 'Current Designation' },
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'age', label: 'Age' },
      { key: 'gender', label: 'Gender' },
      { key: 'marital_status', label: 'Marital Status' },
      { key: 'mother_name', label: 'Mother Name' },
      { key: 'spouse_name', label: 'Spouse Name' }
    ]
  },
  {
    key: 'C',
    name: 'Contact & Address',
    fields: [
      { key: 'permanent_address_line1', label: 'Address Line 1' },
      { key: 'permanent_address_line2', label: 'Address Line 2' },
      { key: 'pin_code', label: 'Pin Code' },
      { key: 'landmark', label: 'Landmark' },
      { key: 'mobile_number', label: 'Mobile Number' },
      { key: 'home_phone', label: 'Home Phone' },
      { key: 'personal_email', label: 'Personal Email' },
      { key: 'work_email', label: 'Work Email' },
      { key: 'other_email', label: 'Other Email' }
    ]
  },
  {
    key: 'D',
    name: 'Identity Documents',
    fields: [
      { key: 'pan_number', label: 'PAN Number' },
      { key: 'aadhar_number', label: 'Aadhar Number' },
      { key: 'passport_number', label: 'Passport Number' },
      { key: 'passport_valid_upto', label: 'Passport Valid Upto' },
      { key: 'uan_number', label: 'UAN Number' },
      { key: 'pf_option', label: 'PF Option' }
    ]
  },
  {
    key: 'E',
    name: 'Bank Details',
    fields: [
      { key: 'account_holder_name', label: 'Account Holder Name' },
      { key: 'bank_name', label: 'Bank Name' },
      { key: 'branch_address', label: 'Branch Address' },
      { key: 'account_number', label: 'Account Number' },
      { key: 'ifsc_code', label: 'IFSC Code' },
      { key: 'account_type', label: 'Account Type' }
    ]
  },
  {
    key: 'G',
    name: 'Emergency Contact',
    fields: [
      { key: 'emergency_contact_name', label: 'Name' },
      { key: 'emergency_contact_phone', label: 'Phone' },
      { key: 'emergency_contact_email', label: 'Email' },
      { key: 'emergency_contact_relationship', label: 'Relationship' }
    ]
  },
  {
    key: 'I',
    name: 'Experience',
    fields: [
      { key: 'experience_in_years', label: 'Experience In Years' },
      { key: 'experience_out_years', label: 'Experience Outside Years' }
    ]
  },
  {
    key: 'K',
    name: 'HR Fields',
    fields: [
      { key: 'employee_status', label: 'Employee Status' },
      { key: 'employee_type', label: 'Employee Type' },
      { key: 'reporting_to', label: 'Reporting To' },
      { key: 'source_of_hire', label: 'Source of Hire' },
      { key: 'department', label: 'Department' },
      { key: 'location', label: 'Location' }
    ]
  }
]

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return 'Not provided'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

function AdminReview() {
  const { companySlug, employeeId } = useParams()
  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [commentDrafts, setCommentDrafts] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [docComments, setDocComments] = useState([])
  const [docCommentDraft, setDocCommentDraft] = useState('')
  const [docSubmitting, setDocSubmitting] = useState(false)
  const [isDownloadingDocsZip, setIsDownloadingDocsZip] = useState(false)

  useEffect(() => {
    fetchDetails()
  }, [companySlug, employeeId])

  const fetchDetails = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/${companySlug}/admin/employees/${employeeId}/details`)
      setDetails(response.data)
    } catch (error) {
      toast.error('Failed to load employee details')
    } finally {
      setLoading(false)
    }
  }

  const apiOrigin = useMemo(() => {
    const base = import.meta.env.VITE_API_URL || window.location.origin
    return base.endsWith('/') ? base.slice(0, -1) : base
  }, [])

  const commentsBySection = useMemo(() => {
    const grouped = {}
    if (!details?.comments) return grouped
    details.comments.forEach((comment) => {
      const key = comment.section_key || 'Unknown'
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(comment)
    })
    return grouped
  }, [details])

  const handleAddComment = async (section) => {
    const message = (commentDrafts[section.key] || '').trim()
    if (!message) return
    try {
      setSubmitting(true)
      const response = await api.post(`/${companySlug}/admin/employees/${employeeId}/comments`, {
        section_key: section.key,
        section_label: section.name,
        comment: message
      })
      setDetails((prev) => ({
        ...prev,
        comments: [response.data, ...(prev?.comments || []).filter((c) => c.section_key !== section.key)]
      }))
      setCommentDrafts((prev) => ({ ...prev, [section.key]: '' }))
      toast.success('Comment added')
    } catch (error) {
      toast.error('Failed to add comment')
    } finally {
      setSubmitting(false)
    }
  }

  const loadDocComments = async (docId) => {
    try {
      const response = await api.get(`/${companySlug}/admin/employees/${employeeId}/documents/${docId}/comments`)
      setDocComments(response.data)
    } catch (error) {
      setDocComments([])
    }
  }

  const handleOpenDoc = async (doc) => {
    setSelectedDoc(doc)
    setDocCommentDraft('')
    await loadDocComments(doc.id)
  }

  const handleAddDocComment = async () => {
    const message = docCommentDraft.trim()
    if (!message || !selectedDoc) return
    try {
      setDocSubmitting(true)
      const response = await api.post(`/${companySlug}/admin/employees/${employeeId}/documents/${selectedDoc.id}/comments`, {
        comment: message
      })
      setDocComments((prev) => [response.data, ...prev])
      setDocCommentDraft('')
      toast.success('Document comment added')
    } catch (error) {
      toast.error('Failed to add document comment')
    } finally {
      setDocSubmitting(false)
    }
  }
  
  const handleExportPDF = async () => {
    try {
      const response = await api.get(`/${companySlug}/exports/personal-info/${employeeId}`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Personal_Info_${employee.full_name || employee.email}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('PDF downloaded')
    } catch (error) {
      toast.error('Failed to export PDF')
    }
  }

  const handleDownloadAllDocuments = async () => {
    if (isDownloadingDocsZip) return
    try {
      setIsDownloadingDocsZip(true)
      const response = await api.get(`/${companySlug}/admin/employees/${employeeId}/documents/download-all`, {
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const fallbackName = `employee_${employeeId}_documents.zip`
      const disposition = response.headers['content-disposition'] || ''
      const filenameMatch = disposition.match(/filename="?([^\"]+)"?/)
      link.setAttribute('download', filenameMatch?.[1] || fallbackName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('Documents zip downloaded')
    } catch (error) {
      toast.error('Failed to download documents zip')
    } finally {
      setIsDownloadingDocsZip(false)
    }
  }

  const buildDocUrl = (docId) => {
    const token = localStorage.getItem('token')
    const safeToken = token ? encodeURIComponent(token) : ''
    return `${apiOrigin}/api/${companySlug}/admin/employees/${employeeId}/documents/${docId}/download?token=${safeToken}`
  }

  const buildOfficeViewerUrl = (docUrl) => {
    const encoded = encodeURIComponent(docUrl)
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encoded}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!details) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-600">Employee not found.</p>
        <Link to={`/${companySlug}/admin`} className="btn-primary mt-4 inline-flex items-center">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Admin
        </Link>
      </div>
    )
  }

  const { employee, family, education, employment, documents } = details

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <Link to={`/${companySlug}/admin`} className="text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Admin Review</h1>
          </div>
          <p className="text-gray-600 mt-1">
            {employee.full_name || 'Employee'} • {employee.email}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExportPDF}
            className="btn-secondary flex items-center"
          >
            <Download className="h-4 w-4 mr-2" /> Download Form
          </button>
          <button
            onClick={handleDownloadAllDocuments}
            className="btn-secondary flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={!documents?.length || isDownloadingDocsZip}
          >
            {isDownloadingDocsZip ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {isDownloadingDocsZip ? 'Preparing ZIP...' : 'Download Docs ZIP'}
          </button>
          {employee.is_locked ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
              <Lock className="h-4 w-4 mr-1" /> Locked
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              <Unlock className="h-4 w-4 mr-1" /> Unlocked
            </span>
          )}
          {employee.is_submitted ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <CheckCircle className="h-4 w-4 mr-1" /> Submitted
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
              <AlertCircle className="h-4 w-4 mr-1" /> In Progress
            </span>
          )}
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents</h3>
        {documents?.length === 0 ? (
          <p className="text-sm text-gray-500">No documents uploaded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Document
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uploaded
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documents.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{doc.original_filename}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{doc.document_type}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {doc.upload_timestamp ? new Date(doc.upload_timestamp).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleOpenDoc(doc)}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {SECTIONS.map((section) => {
        const sectionComments = commentsBySection[section.key] || []
        const latestComment = sectionComments[0]
        const completion = employee.section_completion_status?.[section.key]
        return (
          <div key={section.key} className="card space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {section.key}. {section.name}
              </h3>
              <span className={`text-xs font-medium px-2 py-1 rounded ${completion ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {completion ? 'Completed' : 'Not completed'}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {section.fields.map((field) => (
                <div key={field.key}>
                  <div className="text-xs text-gray-500">{field.label}</div>
                  <div className="text-sm text-gray-900">{formatValue(employee[field.key])}</div>
                </div>
              ))}
            </div>
            <div className="border-t pt-4 space-y-3">
              <div className="text-sm font-medium text-gray-800">Section Comments</div>
              {!latestComment ? (
                <p className="text-sm text-gray-500">No comments for this section.</p>
              ) : (
                <div className="p-3 rounded-lg bg-gray-50 border">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">{latestComment.comment}</div>
                    <span className={`text-xs px-2 py-1 rounded ${latestComment.status === 'rectified' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {latestComment.status === 'rectified' ? 'Rectified' : 'Open'}
                    </span>
                  </div>
                  {latestComment.rectified_note && (
                    <div className="text-xs text-gray-500 mt-1">Employee note: {latestComment.rectified_note}</div>
                  )}
                </div>
              )}
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <textarea
                  value={commentDrafts[section.key] || ''}
                  onChange={(e) => setCommentDrafts((prev) => ({ ...prev, [section.key]: e.target.value }))}
                  className="input flex-1"
                  rows={2}
                  placeholder={`Add comment for ${section.name}...`}
                />
                <button
                  onClick={() => handleAddComment(section)}
                  className="btn-primary"
                  disabled={submitting}
                >
                  Add Comment
                </button>
              </div>
            </div>
          </div>
        )
      })}

      <div className="space-y-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Family Members</h3>
          {family?.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Relationship</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Age</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {family.map((member) => (
                    <tr key={member.id}>
                      <td className="px-3 py-2 text-gray-900">{member.name || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{member.relationship || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{member.age ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No family members added.</p>
          )}
          <div className="border-t pt-4 mt-4">
            <div className="text-sm font-medium text-gray-800 mb-2">Family Section Comment</div>
            {commentsBySection['F']?.[0] ? (
              <div className="p-3 rounded-lg bg-gray-50 border mb-3">
                <div className="text-sm text-gray-700">{commentsBySection['F'][0].comment}</div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-3">No comment yet.</p>
            )}
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <textarea
                value={commentDrafts['F'] || ''}
                onChange={(e) => setCommentDrafts((prev) => ({ ...prev, F: e.target.value }))}
                className="input flex-1"
                rows={2}
                placeholder="Add comment for Family section..."
              />
              <button
                onClick={() => handleAddComment({ key: 'F', name: 'Family Information' })}
                className="btn-primary"
                disabled={submitting}
              >
                Add Comment
              </button>
            </div>
          </div>
        </div>
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Education</h3>
          {education?.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Institution</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Year</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {education.map((item) => (
                    <tr key={item.id}>
                      <td className="px-3 py-2 text-gray-900">{item.qualification_level || item.education_type || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{item.institute_name || item.institution_name || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{item.passing_year || item.year_of_passing || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No education records.</p>
          )}
          <div className="border-t pt-4 mt-4">
            <div className="text-sm font-medium text-gray-800 mb-2">Education Section Comment</div>
            {commentsBySection['H']?.[0] ? (
              <div className="p-3 rounded-lg bg-gray-50 border mb-3">
                <div className="text-sm text-gray-700">{commentsBySection['H'][0].comment}</div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-3">No comment yet.</p>
            )}
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <textarea
                value={commentDrafts['H'] || ''}
                onChange={(e) => setCommentDrafts((prev) => ({ ...prev, H: e.target.value }))}
                className="input flex-1"
                rows={2}
                placeholder="Add comment for Education section..."
              />
              <button
                onClick={() => handleAddComment({ key: 'H', name: 'Education' })}
                className="btn-primary"
                disabled={submitting}
              >
                Add Comment
              </button>
            </div>
          </div>
        </div>
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Employment</h3>
          {employment?.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Years</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employment.map((job) => (
                    <tr key={job.id}>
                      <td className="px-3 py-2 text-gray-900">{job.company_name || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{job.designation || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">
                        {(job.from_date || '—')} - {(job.to_date || '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No employment history.</p>
          )}
          <div className="border-t pt-4 mt-4">
            <div className="text-sm font-medium text-gray-800 mb-2">Employment Section Comment</div>
            {commentsBySection['J']?.[0] ? (
              <div className="p-3 rounded-lg bg-gray-50 border mb-3">
                <div className="text-sm text-gray-700">{commentsBySection['J'][0].comment}</div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-3">No comment yet.</p>
            )}
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <textarea
                value={commentDrafts['J'] || ''}
                onChange={(e) => setCommentDrafts((prev) => ({ ...prev, J: e.target.value }))}
                className="input flex-1"
                rows={2}
                placeholder="Add comment for Employment section..."
              />
              <button
                onClick={() => handleAddComment({ key: 'J', name: 'Previous Employment' })}
                className="btn-primary"
                disabled={submitting}
              >
                Add Comment
              </button>
            </div>
          </div>
        </div>
      </div>

      {selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-5xl h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="text-sm font-medium text-gray-900">{selectedDoc.original_filename}</div>
              <button onClick={() => setSelectedDoc(null)} className="text-gray-500 hover:text-gray-800">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-0">
              <div className="lg:col-span-2 border-r">
                <div className="h-full">
                  {(() => {
                    const docUrl = buildDocUrl(selectedDoc.id)
                    const name = selectedDoc.original_filename
                    const mime = selectedDoc.mime_type || ''
                    const lowerName = name.toLowerCase()
                    const isImage = mime.startsWith('image/') || /\.(png|jpe?g|webp|heic)$/i.test(lowerName)
                    const isPdf = mime === 'application/pdf' || /\.pdf$/i.test(lowerName)
                    const isDoc = mime.includes('word') || /\.(docx?|rtf)$/i.test(lowerName)
                    if (isImage) {
                      return <img src={docUrl} alt={name} className="w-full h-full object-contain bg-gray-50" />
                    }
                    if (isPdf) {
                      return <iframe src={docUrl} className="w-full h-full" title={name} />
                    }
                    if (isDoc) {
                      return <iframe src={buildOfficeViewerUrl(docUrl)} className="w-full h-full" title={`${name}-viewer`} />
                    }
                    return <div className="p-6 text-sm text-gray-500">Preview not available for this file type.</div>
                  })()}
                </div>
              </div>
              <div className="p-4 space-y-3">
                <div className="text-sm font-semibold text-gray-800">Document Comments</div>
                {docComments.length === 0 ? (
                  <p className="text-sm text-gray-500">No comments yet.</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {docComments.map((comment) => (
                      <div key={comment.id} className="p-2 rounded border bg-gray-50">
                        <div className="text-sm text-gray-700">{comment.comment}</div>
                        {comment.rectified_note && (
                          <div className="text-xs text-gray-500 mt-1">Employee note: {comment.rectified_note}</div>
                        )}
                        <div className="text-xs mt-1 text-gray-500">
                          {comment.status === 'rectified' ? 'Rectified' : 'Open'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <textarea
                  value={docCommentDraft}
                  onChange={(e) => setDocCommentDraft(e.target.value)}
                  className="input"
                  rows={3}
                  placeholder="Add a document comment..."
                />
                <button
                  onClick={handleAddDocComment}
                  className="btn-primary w-full"
                  disabled={docSubmitting}
                >
                  Add Comment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default AdminReview
